/**
 * useReducedMotion — listen for the `prefers-reduced-motion` media query.
 *
 * The OS-level accessibility setting is the source of truth for whether
 * decorative animations (chalk-dust drift, ink-bleed reveals, stamp
 * scribble-on, etc.) should fire. Components consume this hook to skip
 * flourishes while preserving essential transitions like modal open/close.
 *
 * Why a custom hook rather than `motion/react`'s built-in? We want a single
 * boolean we can branch on in JSX (e.g. `if (reduced) return <span>{text}</span>`)
 * without paying for animation orchestration when it's just going to be no-op.
 *
 * Returns `false` during SSR or when `matchMedia` is unavailable so the
 * default behaviour is "play animations" — failing-loud for accessibility
 * is worse than failing-quiet for ornament.
 */

import { useEffect, useState } from 'react';

const QUERY = '(prefers-reduced-motion: reduce)';

export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mq = window.matchMedia(QUERY);
    const handler = (event: MediaQueryListEvent) => setReduced(event.matches);
    // Safari < 14 needs the legacy `addListener` fallback, but our build
    // targets ES2022 + modern browsers. addEventListener is universal.
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return reduced;
}
